import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  syncCurrencies,
  refreshCurrenciesRates,
  handleSelectItem,
  handleSelectAll,
  getCurrenciesList,
  handlePaginate,
  updateCurrencyActive,
  setManualCurrencyRate,
} from "store/payments/currencies/slice";
import ActionBtn from "components/buttons/ActionBtn";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import { currenciesLimits } from "configs/limits";

import Pagination from "components/Pagination";
import CurrenciesFilter from "./components/CurrenciesFilter";

import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import refresh from "../../../assets/icons/refresh.svg";
import currency from "../../../assets/icons/currency.svg";
import editIcon from "../../../assets/icons/edit-icon.svg";
import SwitchBtn from "components/buttons/SwitchBtn";
import ConfirmTooltip from "components/confirmTooltip/ConfirmTooltip";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const manualRateDurationOptions = [
  { value: "1", label: "1 minute" },
  { value: "2", label: "2 minutes" },
  { value: "5", label: "5 minutes" },
  { value: "10", label: "10 minutes" },
  { value: "60", label: "1h" },
  { value: "720", label: "12h" },
  { value: "1440", label: "24h" },
];

const isValidManualRate = (value: string) => {
  const parsedValue = Number(value);

  return (
    value.trim() !== "" && Number.isFinite(parsedValue) && parsedValue > 0
  );
};

const CurrenciesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const permissions = usePermissionHelper();
  const canUpdate = permissions?.update ?? false;

  const [order, setOrder] = useState<{
    id?: string;
    code?: string;
    type?: string;
    name?: string;
    symbol?: string;
    scale?: string;
    updatedAt?: string;
  }>({
    id: "desc",
  });

  const [currentLimit, setLimit] = useState<number>(currenciesLimits[0]);
  const [search, setSearch] = useState<string>("");
  const [active, setActive] = useState<
    { value: number | string; label: string }[]
  >([{ value: 3, label: "All" }]);
  const [currencyType, setCurrencyType] = useState<
    { value: string; label: string }[]
  >([{ value: "all", label: "All" }]);
  const [isRefreshingRates, setIsRefreshingRates] = useState(false);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<number | null>(
    null,
  );
  const [activeRateId, setActiveRateId] = useState<number | null>(null);
  const [manualRate, setManualRate] = useState("");
  const [hasManualRateError, setHasManualRateError] = useState(false);
  const [duration, setDuration] = useState("1");
  const list = useAppSelector((store) => store["currencies"].list);

  const isListFetched = useAppSelector(
    (store) => store["currencies"].isListFetched,
  );
  const totalItems = useAppSelector((store) => store["currencies"].totalItems);
  const totalPages = useAppSelector((store) => store["currencies"].totalPages);
  const activePage = useAppSelector((store) => store["currencies"].activePage);
  const isPumpkings = window.location.hostname.includes("pumpkings");

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getCurrenciesList({
          limit: currentLimit,
          offset: activePage - 1,
          currency: search ? search : undefined,
          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,
          currencyTypes:
            currencyType[0]?.value !== "all"
              ? [currencyType[0].value as string]
              : [],
          order: Object.keys(order).length ? order : { id: "desc" },
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, order]);

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | undefined;

    if (!current) next = "asc";
    else if (current === "asc") next = "desc";
    else next = undefined;

    const newOrder = next ? { [key]: next } : {};
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const selectedItems = useAppSelector(
    (store) => store["currencies"].selectedItems,
  );

  const handleSync = async () => {
    if (!canUpdate) return;

    const result = await dispatch(syncCurrencies());

    if (result?.meta?.requestStatus === "fulfilled") {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("currenciesSyncedSuccessfully"),
          type: NotificationTypes.success,
        }),
      );

      dispatch(handlePaginate(1));
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("syncFailed"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const handleRefreshRates = async () => {
    if (!canUpdate) return;

    setIsRefreshingRates(true);

    const result = await dispatch(refreshCurrenciesRates());

    if (result?.meta?.requestStatus === "fulfilled") {
      await dispatch(
        getCurrenciesList({
          limit: currentLimit,
          offset: activePage - 1,
          currency: search ? search : undefined,
          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,
          currencyTypes:
            currencyType[0]?.value !== "all"
              ? [currencyType[0].value as string]
              : [],
          order: Object.keys(order).length ? order : { id: "desc" },
        }),
      );

      dispatch(
        showNotification({
          title: t("success"),
          message: t("ratesRefreshedSuccessfully"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("failedToRefreshRates"),
          type: NotificationTypes.error,
        }),
      );
    }

    setIsRefreshingRates(false);
  };

  const handleManualRateSave = async (currencyFrom: string) => {
    if (!canUpdate) return;

    const parsedRate = Number(manualRate);

    if (!isValidManualRate(manualRate)) {
      setHasManualRateError(true);
      return;
    }

    const result = await dispatch(
      setManualCurrencyRate({
        currencyFrom,
        baseCurrency: baseCurrency || "",
        rate: parsedRate,
        expiresInMinutes: Number(duration),
      }),
    );

    if (result.meta.requestStatus === "fulfilled") {
      dispatch(handlePaginate(activePage));

      dispatch(
        showNotification({
          title: t("success"),
          message: t("manualRateUpdatedSuccessfully"),
          type: NotificationTypes.success,
        }),
      );

      setActiveRateId(null);
      setManualRate("");
      setHasManualRateError(false);
      setDuration("1");
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: "Failed to update manual rate",
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const baseCurrency = useAppSelector(
    (store) => store["currencies"].baseCurrency,
  );

  const [activeConfirmId, setActiveConfirmId] = useState<number | null>(null);

  const formattedList = list.map((item) => ({
    id: item.id,
    code: item.code,
    type: item.type,
    name: item.name,
    scale: item.scale,
    rate: item.code === baseCurrency ? 1 : (item.rate ?? "-"),
    isActive: item.isActive,
    source: item.provider ?? "-",
    updatedAt: item.updatedAt,
  }));

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <CurrenciesFilter
        handleApply={() => {
          dispatch(handlePaginate(1));
        }}
        searchValue={search}
        handleSearch={setSearch}
        selectedActive={active}
        handleUpdateActive={setActive}
        selectedCurrencyType={currencyType}
        handleUpdateCurrencyType={setCurrencyType}
      />

      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box page-list-header--box-actions">
            {isPumpkings && canUpdate && (
              <ActionBtn
                txt="Sync"
                mainIcon={refresh}
                handleClick={handleSync}
                tooltip={t("syncCurrencies")}
              />
            )}

            {canUpdate && (
              <ActionBtn
                txt="Rates"
                handleClick={handleRefreshRates}
                mainIcon={currency}
                isLoad={isRefreshingRates}
                tooltip={t("refreshCurrencyRates")}
              />
            )}

            <DefaultDropdown
              onSelect={(e) => {}}
              placeholder={t("chooseAction")}
              options={[]}
              isSmall
              disabled={true}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={10} rowsCount={15} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <div className="game-table-wrap">
            {list.length === 0 && (
              <EmptyState message={t("noCurrenciesFound")} />
            )}

            {list.length > 0 && (
              <Table
                data={formattedList}
                activeRowId={activeRateId}
                idField="id"
                order={order}
                hasOptions={false}
                columns={{
                  sortingColumns: {
                    id: { value: "ID", hasSorting: true },
                    code: { value: "Code", hasSorting: true },
                    name: { value: "Name", hasSorting: true },
                    type: { value: "Type", hasSorting: true },
                    scale: { value: "Scale", hasSorting: true },
                    rate: { value: "Rates" },
                    isActive: { value: "Active", hasSorting: true },
                    source: { value: "Source" },
                    updatedAt: { value: "Updated At", hasSorting: true },
                  },
                  hiddenColumns: ["scale"],
                }}
                selectedItems={selectedItems}
                hasCheckbox={canUpdate}
                onSelect={(id) => dispatch(handleSelectItem(id))}
                onAllSelect={() => dispatch(handleSelectAll())}
                onSort={(id) => (id ? handleOrderClick(id as any) : {})}
                customColumns={(row) => ({
                  rate: (
                    <div className="currency-rate-cell">
                      <span>{row.rate}</span>

                      {canUpdate && (
                        <>
                          <div
                            data-tooltip-id={`rate-tooltip-${row.id}`}
                            data-tooltip-place="right"
                            className="currency-edit-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setManualRate(String(row.rate));
                              setHasManualRateError(false);
                              setActiveRateId(row.id);
                            }}
                          >
                            <img src={editIcon} alt="edit" />
                          </div>
                          <ConfirmTooltip
                            id={`rate-tooltip-${row.id}`}
                            title={`${t("manualRate")}: ${row.code}`}
                            isOpen={activeRateId === row.id}
                            closeOnConfirm={false}
                            onToggle={(open) => {
                              setActiveRateId(open ? row.id : null);
                              if (!open) setHasManualRateError(false);
                            }}
                            onConfirm={() => handleManualRateSave(row.code)}
                          >
                            <div className="currency-rate-tooltip">
                              <InnerInput
                                id={`rate-${row.id}`}
                                type="number"
                                value={manualRate}
                                placeholder="Rate"
                                hasError={hasManualRateError}
                                inputMode="decimal"
                                min={0}
                                onChange={(value) => {
                                  setManualRate(value);

                                  if (isValidManualRate(value)) {
                                    setHasManualRateError(false);
                                  }
                                }}
                              />

                              <DefaultDropdown
                                value={duration}
                                onSelect={(value) => setDuration(String(value))}
                                options={manualRateDurationOptions}
                              />
                            </div>
                          </ConfirmTooltip>
                        </>
                      )}
                    </div>
                  ),
                  isActive: (
                    <div
                      data-tooltip-id={`currency-tooltip-${row.id}`}
                      className="currency-switch"
                    >
                      <SwitchBtn
                        isActive={row.isActive}
                        disabled={!canUpdate}
                        handleClick={() => {
                          setActiveConfirmId(row.id);
                        }}
                        activeTxt={t("on")}
                        notActiveTxt={t("off")}
                      />
                      {canUpdate && (
                        <ConfirmTooltip
                          id={`currency-tooltip-${row.id}`}
                          title={
                            row.isActive
                              ? t("areYouSureYouWantToDisableThisCurrency")
                              : t("areYouSureYouWantToEnableThisCurrency")
                          }
                          isOpen={activeConfirmId === row.id}
                          onToggle={(open) => {
                            setActiveConfirmId(open ? row.id : null);
                          }}
                          onConfirm={async () => {
                            if (!canUpdate) return;

                            const result = await dispatch(
                              updateCurrencyActive({
                                currencyId: row.id,
                                isActive: row.isActive ? 0 : 1,
                              }),
                            );

                            if (result.meta.requestStatus === "fulfilled") {
                              dispatch(handlePaginate(activePage));

                              dispatch(
                                showNotification({
                                  title: t("success"),
                                  message: t("currencyStatusUpdated"),
                                  type: NotificationTypes.success,
                                }),
                              );
                            } else {
                              dispatch(
                                showNotification({
                                  title: t("error"),
                                  message: t("failedToUpdateCurrency"),
                                  type: NotificationTypes.error,
                                }),
                              );
                            }

                            setActiveConfirmId(null);
                          }}
                          config={{
                            clickable: true,
                            openEvents: { click: true },
                            closeEvents: { click: true, blur: true },
                            position: "left",
                          }}
                        />
                      )}
                    </div>
                  ),
                })}
              />
            )}
          </div>
        )}

        {isListFetched && list.length > 0 && (
          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);
              dispatch(handlePaginate(1));
            }}
            limitsArray={currenciesLimits}
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        )}
      </div>
    </div>
  );
};

export default CurrenciesList;
